export interface WebSocketConfig {
  url: string;
  reconnectInterval: number;
  maxReconnectAttempts: number;
  timeout: number;
  heartbeatInterval: number;
  messageQueueSize: number;
}

export interface WebSocketMessage<T = any> {
  id: string;
  type: string;
  timestamp: number;
  data: T;
  retry?: boolean;
}

export interface QueuedMessage extends WebSocketMessage {
  attempts: number;
  maxAttempts: number;
}

export enum WebSocketEventType {
  // Connection events
  CONNECT = 'connect',
  DISCONNECT = 'disconnect',
  RECONNECT = 'reconnect',
  ERROR = 'error',
  
  // Price monitoring events
  PRICE_UPDATE = 'price_update',
  COMPETITOR_PRICE = 'competitor_price',
  PRICE_ALERT = 'price_alert',
  MARKET_CHANGE = 'market_change',
  
  // System events
  SYSTEM_ALERT = 'system_alert',
  RULE_TRIGGERED = 'rule_triggered',
  ANALYTICS_UPDATE = 'analytics_update',
  ORDER_CREATED = 'order.created',
  PRICE_UPDATED = 'price.updated',
  REPRICING_COMPLETED = 'repricing.completed',
  COMPETITOR_CHANGED = 'competitor.changed',
  
  // Subscription events
  SUBSCRIBE_PRODUCT = 'subscribe_product',
  UNSUBSCRIBE_PRODUCT = 'unsubscribe_product',
  SUBSCRIBE_DASHBOARD = 'subscribe_dashboard',
  UNSUBSCRIBE_DASHBOARD = 'unsubscribe_dashboard',
  
  // Heartbeat
  PING = 'ping',
  PONG = 'pong'
}

export interface PriceUpdateMessage {
  productId: string;
  currentPrice: number;
  previousPrice: number;
  changePercent: number;
  timestamp: number;
  source: 'system' | 'competitor' | 'manual';
}

export interface CompetitorPriceMessage {
  productId: string;
  competitorId: string;
  competitorName: string;
  price: number;
  previousPrice: number;
  priceChange: number;
  timestamp: number;
  marketplace: string;
}

export interface PriceAlertMessage {
  id: string;
  productId: string;
  alertType: 'price_drop' | 'price_increase' | 'competitor_undercut' | 'margin_warning';
  message: string;
  severity: 'info' | 'warning' | 'critical';
  currentPrice: number;
  targetPrice?: number;
  competitorPrice?: number;
  timestamp: number;
}

export interface MarketChangeMessage {
  productIds: string[];
  changeType: 'trend_up' | 'trend_down' | 'volatility_high' | 'market_shift';
  description: string;
  impact: 'low' | 'medium' | 'high';
  timestamp: number;
  metadata?: Record<string, any>;
}

export interface AnalyticsUpdateMessage {
  type: 'revenue' | 'profit' | 'competition' | 'performance';
  data: Record<string, any>;
  timeframe: 'hourly' | 'daily' | 'weekly';
  timestamp: number;
}

export interface SystemAlertMessage {
  id: string;
  type: 'info' | 'warning' | 'error' | 'success';
  title: string;
  message: string;
  timestamp: number;
  actionRequired?: boolean;
  actionUrl?: string;
}

export interface OrderCreatedEvent {
  orderId: string;
  externalOrderId?: string;
  marketplace?: string;
  totalRevenue?: number;
  createdAt?: string;
}

export interface PriceUpdatedEvent {
  productId: string;
  marketplace?: string;
  oldPrice?: number;
  newPrice: number;
  currency?: string;
  updatedAt?: string;
}

export interface RepricingCompletedEvent {
  updatedCount: number;
  totalCount?: number;
  completedAt?: string;
}

export interface CompetitorChangedEvent {
  productId: string;
  competitorName?: string;
  newPrice?: number;
  changedAt?: string;
}

export interface WebSocketConnectionStatus {
  isConnected: boolean;
  connectionAttempts: number;
  lastConnected?: number;
  lastDisconnected?: number;
  isReconnecting: boolean;
  queuedMessages: number;
}

export interface SubscriptionOptions {
  productIds?: string[];
  competitorIds?: string[];
  alertTypes?: string[];
  batchSize?: number;
  throttleMs?: number;
}

// Server-side WebSocket message types
export type ServerMessageType =
  | 'connected'
  | 'authenticated'
  | 'subscribed'
  | 'unsubscribed'
  | 'error'
  | 'event'
  | 'pong'
  | 'info';

export interface ServerConnectedMessage {
  type: 'connected';
  connection_id: string;
  authenticated: boolean;
  timestamp: string;
}

export interface ServerAuthenticatedMessage {
  type: 'authenticated';
  user_id: string;
  tenant_slug?: string;
  timestamp: string;
}

export interface ServerSubscribedMessage {
  type: 'subscribed';
  channel: string;
  timestamp: string;
}

export interface ServerUnsubscribedMessage {
  type: 'unsubscribed';
  channel: string;
  timestamp: string;
}

export interface ServerErrorMessage {
  type: 'error';
  message: string;
  code?: string;
}

export interface ServerEventMessage<T = any> {
  id?: string;
  type: 'event';
  event: string;
  data: T;
  timestamp: string;
}

export interface ServerBroadcastMessage<T = any> {
  type: 'broadcast';
  channel: string;
  data: ServerEventMessage<T>;
  timestamp: string;
}

export interface ServerPongMessage {
  type: 'pong';
  timestamp: string;
}

export type ServerMessage =
  | ServerConnectedMessage
  | ServerAuthenticatedMessage
  | ServerSubscribedMessage
  | ServerUnsubscribedMessage
  | ServerErrorMessage
  | ServerEventMessage
  | ServerBroadcastMessage
  | ServerPongMessage;

export interface DeltaEventsResponse<T = any> {
  events: Array<{
    id: string;
    event: string;
    data: T;
    timestamp: string;
    channel?: string | null;
  }>;
}

// Client-side WebSocket message types
export type ClientMessageType =
  | 'ping'
  | 'subscribe'
  | 'unsubscribe'
  | 'subscribe_dashboard'
  | 'subscribe_prices'
  | 'subscribe_alerts';

export interface ClientPingMessage {
  type: 'ping';
}

export interface ClientSubscribeMessage {
  type: 'subscribe' | `subscribe_${string}`;
  channel?: string;
}

export interface ClientUnsubscribeMessage {
  type: 'unsubscribe' | `unsubscribe_${string}`;
  channel?: string;
}

export type ClientMessage =
  | ClientPingMessage
  | ClientSubscribeMessage
  | ClientUnsubscribeMessage;
